import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonChip,
  IonButton,
  IonIcon,
  IonRefresher,
  IonRefresherContent,
  IonToast,
  RefresherEventDetail,
} from '@ionic/react';
import {
  bookmarkOutline,
  bookmark,
  chevronDownOutline,
  chevronUpOutline,
  sparklesOutline,
  briefcaseOutline,
  personOutline,
  documentTextOutline,
} from 'ionicons/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { Bill, BillSponsor } from '../../types/bill';
import ProPositionSheet from '../../components/pro/bottom-sheet/ProPositionSheet';
import ProTrackingWorkspacePicker from '../../components/pro/bottom-sheet/ProTrackingWorkspacePicker';
import ProTrackingDestinationLabel from '../../components/pro/ProTrackingDestinationLabel';
import { ProLoadingList } from '../../components/pro/primitives';
import { getPositionColorClasses, getPositionLabel } from '../../utils/positionColors';
import { formatBillStatusShort } from '../../utils/formatBillStatus';
import { formatPartyDisplay } from '../../utils/partyUtils';
import { useProTrackingWorkspace } from './useProTrackingWorkspace';
import { unwrapProBillDetail } from './unwrapProBillDetail';
import { trackingCapabilitiesFromAccessLevel } from './trackingCapabilitiesFromAccessLevel';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LOBBYIST_INITIAL_COUNT = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isUsefulText = (s: string | undefined | null): s is string =>
  !!s && s.trim() !== '' && s.trim().toLowerCase() !== 'n/a';

const getStatusColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s.includes('signed') || s.includes('enacted')) return 'success';
  if (s.includes('passed') || s.includes('approved')) return 'primary';
  if (s.includes('failed') || s.includes('vetoed')) return 'danger';
  if (s.includes('committee') || s.includes('hearing')) return 'warning';
  return 'medium';
};

// Party affiliation rendered as text-only — no chip background, just a tinted
// glyph that hugs the sponsor name. `pl-1` left-pads it off the name; no right
// padding so it shrinks against whatever container edge follows.
const getPartyTextClasses = (party: string): string => {
  const p = (party || '').toLowerCase();
  if (p === 'r' || p === 'republican') return 'text-red-600 dark:text-red-400';
  if (p === 'd' || p === 'democrat' || p === 'democratic') return 'text-primary-600 dark:text-primary-300';
  return 'text-text-tertiary dark:text-text-dark-tertiary';
};

interface KeyDetailRowProps {
  label: string;
  children: React.ReactNode;
  button?: boolean;
  onClick?: () => void;
}

/** Label-over-value row for mobile — avoids IonItem slot="end" column squeeze. */
const KeyDetailRow: React.FC<KeyDetailRowProps> = ({ label, children, button, onClick }) => (
  <IonItem button={button} onClick={onClick}>
    <IonLabel className="ion-text-wrap">
      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wide mb-0.5">
        {label}
      </p>
      {children}
    </IonLabel>
  </IonItem>
);


// ---------------------------------------------------------------------------
// Tracking pending action (needs-choice → picker → resume write)
// ---------------------------------------------------------------------------

type PendingTrackingAction =
  | { kind: 'track' }
  | { kind: 'untrack' }
  | { kind: 'position'; payload: string | null }
  | { kind: 'open-position' };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ProBillDetailPage: React.FC = () => {
  const { t } = useTranslation('pro');
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const location = useLocation();

  // ── UI State ───────────────────────────────────────────────────────────────
  const [showToast, setShowToast] = useState('');
  const [aiExpanded, setAiExpanded] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [lobbyistsExpanded, setLobbyistsExpanded] = useState(false);
  const [positionSelectorOpen, setPositionSelectorOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingTrackingAction | null>(null);

  // Router state key shared with ProWorkspaceDetailPage (Task 5): trackingWorkspaceId
  const contextId =
    (location.state as { trackingWorkspaceId?: string } | null)?.trackingWorkspaceId ??
    null;

  const tracking = useProTrackingWorkspace(contextId);
  const { status, eligible, select } = tracking;
  const selectedWorkspace =
    tracking.status === 'selected' ? tracking.workspace : null;
  const workspaceId = selectedWorkspace?.id ?? null;
  const trackingControlsDisabled =
    status === 'none' || status === 'context-ineligible';
  const { canWrite, canManage } = trackingCapabilitiesFromAccessLevel(
    selectedWorkspace?.access_level,
  );
  // Write actions (track/untrack) stay available for contributors; position
  // requires canManage. When no workspace is selected yet (needs-choice),
  // leave buttons enabled so the picker can run first.
  const writeControlsDisabled =
    trackingControlsDisabled || (status === 'selected' && !canWrite);
  const positionControlsDisabled =
    trackingControlsDisabled || (status === 'selected' && !canManage);
  const positionUnavailableExplanation =
    status === 'selected' && !canManage
      ? t('billDetail.positionRequiresManage')
      : null;

  // ── Bill Query ─────────────────────────────────────────────────────────────
  const {
    data: bill,
    isLoading,
    refetch,
  } = useQuery<Bill & Record<string, any>>(
    ['pro-bill', billId],
    async () => {
      const response = await api.get(`/bills/${billId}`);
      // Authenticated detail nests the row under data.bill; flat unwrap leaves id undefined.
      return unwrapProBillDetail(response.data) as Bill & Record<string, any>;
    },
    { staleTime: 60000 }
  );

  // ── Tracking Status Query ──────────────────────────────────────────────────
  const { data: trackingStatus } = useQuery(
    ['pro-bill-tracking', billId, workspaceId],
    async () => {
      if (!workspaceId) return null;
      const { data } = await api.get(`/workspace-bill-tracking/workspace/${workspaceId}`);
      const result = data.data;
      const trackedArray = Array.isArray(result)
        ? result
        : result?.bills || result?.items || [];
      return trackedArray.find((t: any) =>
        (t.bill_number || t.billNumber) === billId
      ) || null;
    },
    { enabled: !!workspaceId }
  );

  const isTracked = !!trackingStatus;
  const currentPosition: string | null = trackingStatus?.position || trackingStatus?.billPosition || null;

  // ── Cache invalidation (detail + list / workspace lists — AC6) ─────────────
  const invalidateBillTrackingCaches = (wsId: string | null | undefined) => {
    if (!wsId) return;
    queryClient.invalidateQueries(['pro-bill', billId]);
    queryClient.invalidateQueries(['pro-bill-tracking', billId, wsId]);
    queryClient.invalidateQueries(['pro-tracked-bills', wsId]);
    queryClient.invalidateQueries(['pro-tracked-legislators', wsId]);
    queryClient.invalidateQueries(['pro-workspace-bills', wsId]);
    queryClient.invalidateQueries(['pro-workspace-legislators', wsId]);
  };

  // ── Track Mutation ─────────────────────────────────────────────────────────
  const trackMutation = useMutation(
    ({ action, wsId }: { action: 'track' | 'untrack'; wsId: string }) => {
      if (!bill?.id) {
        return Promise.reject(new Error('Bill id unavailable'));
      }
      return action === 'track'
        ? api.post(`/workspace-bill-tracking/${wsId}`, { billId: bill.id })
        : api.delete(`/workspace-bill-tracking/${wsId}/${bill.id}`);
    },
    {
      onSuccess: (_data, { action, wsId }) => {
        invalidateBillTrackingCaches(wsId);
        setShowToast(action === 'track' ? t('billDetail.trackAdded') : t('billDetail.trackRemoved'));
      },
      onError: () => {
        setShowToast(t('billDetail.trackError'));
      },
    }
  );

  // ── Position Mutation ──────────────────────────────────────────────────────
  const positionMutation = useMutation(
    ({ position, wsId }: { position: string | null; wsId: string }) => {
      if (!bill?.id) {
        return Promise.reject(new Error('Bill id unavailable'));
      }
      return api.put(`/workspace-bill-tracking/position/${wsId}/${bill.id}`, { position });
    },
    {
      onSuccess: (_data, { wsId }) => {
        invalidateBillTrackingCaches(wsId);
        setShowToast(t('billDetail.positionUpdated'));
      },
      onError: () => {
        setShowToast(t('billDetail.positionError'));
      },
    }
  );

  // ── Derived State ──────────────────────────────────────────────────────────
  const aiSummary = bill?.aiSummary as any;
  const aiShortSummary = bill?.aiShortSummary;
  const hasAiContent = !!(aiShortSummary || aiSummary);
  // Preview text shown when the AI Summary card is collapsed. Cascade:
  // explicit short summary -> nested short summary -> first lines of full.
  const aiPreviewText: string | null =
    (isUsefulText(aiShortSummary) ? aiShortSummary : null) ??
    (aiSummary && isUsefulText(aiSummary.short_summary) ? aiSummary.short_summary : null) ??
    (aiSummary && isUsefulText(aiSummary.full_summary) ? aiSummary.full_summary : null);

  const fullSummaryText =
    isUsefulText(bill?.summary) ? bill.summary
    : isUsefulText(bill?.concerning) ? bill.concerning
    : null;

  const lobbyists: any[] = bill?.lobbyists || [];
  const supportCount = lobbyists.filter(l => l.position?.toLowerCase() === 'support').length;
  const opposeCount = lobbyists.filter(l => l.position?.toLowerCase() === 'oppose').length;
  const displayedLobbyists = lobbyistsExpanded ? lobbyists : lobbyists.slice(0, LOBBYIST_INITIAL_COUNT);

  const sponsors: BillSponsor[] = bill?.sponsors || [];
  const primarySponsor = sponsors.find(s => s.sponsor_type?.toLowerCase() === 'primary') || sponsors[0];
  const committees = bill?.committees || [];

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRefresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  const workspaceCanManage = (wsId: string): boolean => {
    const fromEligible = eligible.find((w) => w.id === wsId);
    const access =
      fromEligible?.access_level ??
      (selectedWorkspace?.id === wsId ? selectedWorkspace.access_level : undefined);
    return trackingCapabilitiesFromAccessLevel(access).canManage;
  };

  const applyPositionInWorkspace = (wsId: string, position: string | null) => {
    // Hard stop: never track-then-position when the destination lacks manage.
    if (!workspaceCanManage(wsId)) {
      setShowToast(t('billDetail.positionRequiresManage'));
      return;
    }
    if (!bill?.id) return;
    if (!isTracked) {
      // Track first, then set position — keep path-embedded workspace id shape.
      api
        .post(`/workspace-bill-tracking/${wsId}`, { billId: bill.id })
        .then(() => {
          invalidateBillTrackingCaches(wsId);
          if (position) positionMutation.mutate({ position, wsId });
        })
        .catch(() => setShowToast(t('billDetail.trackBillError')));
    } else {
      positionMutation.mutate({ position, wsId });
    }
  };

  const executePendingAction = (wsId: string, pending: PendingTrackingAction) => {
    if (pending.kind === 'track' || pending.kind === 'untrack') {
      trackMutation.mutate({ action: pending.kind, wsId });
      return;
    }
    if (pending.kind === 'open-position') {
      if (!workspaceCanManage(wsId)) {
        setShowToast(t('billDetail.positionRequiresManage'));
        return;
      }
      setPositionSelectorOpen(true);
      return;
    }
    if (pending.kind === 'position') {
      applyPositionInWorkspace(wsId, pending.payload);
    }
  };

  const openPickerOr = (pending: PendingTrackingAction, run: () => void) => {
    // Prompt during Track flow when destination is unset OR when multiple
    // workspaces exist and the user is starting a track (not untrack) — so
    // destination is confirmed in-flow, not only via the separate dropdown.
    const shouldPromptInFlow =
      status === 'needs-choice' ||
      (pending.kind === 'track' && eligible.length > 1 && !contextId);

    if (shouldPromptInFlow) {
      setPendingAction(pending);
      setPickerOpen(true);
      return;
    }
    if (status === 'none') {
      setShowToast(t('trackingWorkspace.noneHint'));
      return;
    }
    if (status !== 'selected' || !workspaceId) return;
    run();
  };

  const handleTrackToggle = () => {
    const action = isTracked ? 'untrack' : 'track';
    openPickerOr({ kind: action }, () => {
      if (!workspaceId) return;
      trackMutation.mutate({ action, wsId: workspaceId });
    });
  };

  const handlePositionSelect = (position: string | null) => {
    setPositionSelectorOpen(false);
    openPickerOr({ kind: 'position', payload: position }, () => {
      if (!workspaceId) return;
      applyPositionInWorkspace(workspaceId, position);
    });
  };

  const handlePickerSelect = (nextWorkspaceId: string) => {
    const pending = pendingAction;
    setPendingAction(null);
    select(nextWorkspaceId);
    setPickerOpen(false);
    if (pending) {
      executePendingAction(nextWorkspaceId, pending);
    } else {
      queryClient.invalidateQueries(['pro-bill-tracking', billId]);
    }
  };

  const handlePickerDismiss = () => {
    setPendingAction(null);
    setPickerOpen(false);
  };

  const handleOpenPositionSelector = () => {
    if (trackingControlsDisabled) return;
    if (status === 'selected') {
      if (!canManage) {
        // Viewing the unavailable control must not open the sheet or write.
        setShowToast(t('billDetail.positionRequiresManage'));
        return;
      }
      setPositionSelectorOpen(true);
      return;
    }
    if (status === 'needs-choice') {
      // Resolve destination first; open sheet only if that workspace can manage.
      setPendingAction({ kind: 'open-position' });
      setPickerOpen(true);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent />
      </IonRefresher>

      {isLoading ? (
        <div className="px-4 py-4 max-w-4xl mx-auto">
          <ProLoadingList variant="card" count={3} className="space-y-4" />
        </div>
      ) : bill ? (
        <div className="px-4 py-2 max-w-4xl mx-auto pb-24">

          {/* ── Header Section ──────────────────────────────────── */}
          <div className="mb-4">
            {/* Bill number */}
            <h1 className="text-2xl font-bold text-orange-400 mb-1">
              {bill.billNumber || bill.bill_number || bill.number}
            </h1>

            {/* Status badge */}
            {(bill.status || bill.bill_status) && (
              <IonChip
                color={getStatusColor(bill.status || bill.bill_status)}
                className="text-xs mb-2 h-auto whitespace-normal ion-text-wrap max-w-full"
              >
                {formatBillStatusShort(bill.status || bill.bill_status)}
              </IonChip>
            )}

            {/* Full title */}
            <p className="text-base text-text-secondary dark:text-text-dark-secondary leading-snug">
              {bill.title || bill.bill_title}
            </p>

            {/* Action row — destination + Set Position + Track */}
            <div className="mt-3 flex flex-col gap-2">
              <ProTrackingDestinationLabel
                status={status}
                workspace={selectedWorkspace}
                eligibleCount={eligible.length}
                onPress={
                  contextId
                    ? undefined
                    : () => {
                        setPendingAction(null);
                        setPickerOpen(true);
                      }
                }
                interactive={!contextId}
                disabled={trackingControlsDisabled}
              />
              <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                {positionControlsDisabled && status === 'selected' ? (
                  <div className="inline-flex flex-col items-start gap-0.5">
                    {currentPosition ? (
                      <span
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPositionColorClasses(currentPosition)}`}
                        data-testid="position-readonly"
                      >
                        {getPositionLabel(currentPosition)}
                      </span>
                    ) : (
                      <span
                        className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-secondary text-text-tertiary dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary border border-dashed border-border-medium dark:border-border-dark opacity-50"
                        data-testid="position-unavailable"
                      >
                        {t('common.setPosition')}
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleOpenPositionSelector}
                    disabled={positionControlsDisabled}
                    className={`inline-flex items-center ${positionControlsDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="position-open"
                  >
                    {currentPosition ? (
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${getPositionColorClasses(currentPosition)}`}>
                        {getPositionLabel(currentPosition)}
                      </span>
                    ) : (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-background-secondary text-text-tertiary dark:bg-background-dark-secondary/50 dark:text-text-dark-tertiary border border-dashed border-border-medium dark:border-border-dark">
                        {t('common.setPosition')}
                      </span>
                    )}
                  </button>
                )}

                {/* Track / Untrack button */}
                <button
                  onClick={handleTrackToggle}
                  disabled={writeControlsDisabled || trackMutation.isLoading}
                  className={`flex-shrink-0 flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 rounded-lg transition-colors ${
                    isTracked
                      ? 'text-orange-400'
                      : 'text-text-tertiary dark:text-text-dark-tertiary'
                  } ${writeControlsDisabled || trackMutation.isLoading ? 'opacity-40 cursor-not-allowed' : 'active:scale-95'}`}
                >
                  <IonIcon icon={isTracked ? bookmark : bookmarkOutline} className="text-2xl" />
                  <span className="text-[9px] font-medium leading-none">
                    {isTracked ? t('common.tracked') : t('common.track')}
                  </span>
                </button>
              </div>
              {positionUnavailableExplanation && (
                <p
                  className="text-[11px] leading-snug text-text-tertiary dark:text-text-dark-tertiary"
                  data-testid="position-permission-hint"
                >
                  {positionUnavailableExplanation}
                </p>
              )}
              </div>
            </div>
          </div>

          {/* ── AI Summary Card ─────────────────────────────────── */}
          {hasAiContent && (
            <IonCard className="mb-3">
              <IonCardHeader
                className="cursor-pointer pb-2"
                onClick={() => setAiExpanded(!aiExpanded)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IonIcon icon={sparklesOutline} color="primary" />
                    <IonCardTitle className="text-base">{t('billDetail.aiSummary')}</IonCardTitle>
                  </div>
                  <IonIcon
                    icon={aiExpanded ? chevronUpOutline : chevronDownOutline}
                    className="text-lg text-text-tertiary dark:text-text-dark-tertiary"
                  />
                </div>
              </IonCardHeader>
              <IonCardContent>
                {/* Preview text — always visible. Clamped to 4 lines while
                    collapsed so the user gets a meaningful at-a-glance read
                    without having to expand the card. */}
                {aiPreviewText && (
                  <p
                    className={`text-sm text-text-primary dark:text-text-dark-primary leading-relaxed ${
                      aiExpanded ? '' : 'line-clamp-4'
                    }`}
                  >
                    {aiPreviewText}
                  </p>
                )}

                {/* Expanded: extra AI content not already shown as preview. */}
                {aiExpanded && aiSummary && (
                  <div className="mt-3 space-y-3">
                    {aiSummary.full_summary && aiSummary.full_summary !== aiPreviewText && (
                      <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed whitespace-pre-line">
                        {aiSummary.full_summary}
                      </p>
                    )}
                    {aiSummary.pro_position_summary && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-green-600 dark:text-green-400 mb-1">
                          {t('positions.supportingArguments')}
                        </p>
                        <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                          {aiSummary.pro_position_summary}
                        </p>
                      </div>
                    )}
                    {aiSummary.con_position_summary && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400 mb-1">
                          {t('positions.opposingArguments')}
                        </p>
                        <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
                          {aiSummary.con_position_summary}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Expand/collapse toggle for full AI content */}
                {aiSummary && (aiSummary.full_summary || aiSummary.pro_position_summary || aiSummary.con_position_summary) && (
                  <IonButton
                    fill="clear"
                    size="small"
                    className="mt-1 -ml-2"
                    onClick={() => setAiExpanded(!aiExpanded)}
                  >
                    <IonIcon slot="start" icon={aiExpanded ? chevronUpOutline : chevronDownOutline} />
                    {aiExpanded ? t('common.showLess') : t('common.showMore')}
                  </IonButton>
                )}
              </IonCardContent>
            </IonCard>
          )}

          {/* ── Key Details Card ─────────────────────────────────── */}
          <IonCard className="mb-3">
            <IonCardHeader className="pb-2">
              <IonCardTitle className="text-base">{t('billDetail.keyDetails')}</IonCardTitle>
            </IonCardHeader>
            <IonCardContent className="pt-0">
              <IonList
                lines="inset"
                style={{ background: 'transparent', '--ion-item-background': 'transparent' } as React.CSSProperties}
              >
                {(bill.chamber || bill.location) && (
                  <KeyDetailRow label={t('billDetail.chamber')}>
                    <p className="text-sm text-text-primary dark:text-text-dark-primary">
                      {bill.chamber || bill.location}
                    </p>
                  </KeyDetailRow>
                )}
                {bill.session && (
                  <KeyDetailRow label={t('billDetail.session')}>
                    <p className="text-sm text-text-primary dark:text-text-dark-primary">
                      {bill.session}
                    </p>
                  </KeyDetailRow>
                )}
                {bill.introducedDate && (
                  <KeyDetailRow label={t('billDetail.introduced')}>
                    <p className="text-sm text-text-primary dark:text-text-dark-primary">
                      {new Date(bill.introducedDate).toLocaleDateString()}
                    </p>
                  </KeyDetailRow>
                )}
                {primarySponsor && (
                  <KeyDetailRow
                    label={t('billDetail.primarySponsor')}
                    button={!!primarySponsor.legislator_id}
                    onClick={() => {
                      if (primarySponsor.legislator_id) {
                        navigate(`/pro/legislators/${primarySponsor.legislator_id}`);
                      }
                    }}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm text-text-primary dark:text-text-dark-primary min-w-0">
                        {primarySponsor.name}
                      </p>
                      {primarySponsor.party && (
                        <span className={`flex-shrink-0 text-xs font-semibold ${getPartyTextClasses(primarySponsor.party)}`}>
                          {formatPartyDisplay(primarySponsor.party)}
                        </span>
                      )}
                    </div>
                  </KeyDetailRow>
                )}
                {committees.length > 0 && (
                  <KeyDetailRow label={t('billDetail.committee')}>
                    <p className="text-sm text-text-primary dark:text-text-dark-primary">
                      {committees.map((c: any) => c.name || c).join(', ')}
                    </p>
                  </KeyDetailRow>
                )}
                {isUsefulText(bill.lastActivity) && (
                  <KeyDetailRow label={t('billDetail.latestAction')}>
                    <p className="text-sm text-text-primary dark:text-text-dark-primary leading-snug">
                      {formatBillStatusShort(bill.lastActivity)}
                    </p>
                  </KeyDetailRow>
                )}
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* ── Full Summary (collapsed by default) ──────────────── */}
          {fullSummaryText && (
            <IonCard className="mb-3">
              <IonCardHeader
                className="cursor-pointer"
                onClick={() => setSummaryExpanded(!summaryExpanded)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IonIcon icon={documentTextOutline} color="primary" />
                    <IonCardTitle className="text-base">{t('billDetail.fullSummary')}</IonCardTitle>
                  </div>
                  <IonIcon
                    icon={summaryExpanded ? chevronUpOutline : chevronDownOutline}
                    className="text-lg text-text-tertiary dark:text-text-dark-tertiary"
                  />
                </div>
              </IonCardHeader>
              {summaryExpanded && (
                <IonCardContent>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary whitespace-pre-line leading-relaxed">
                    {fullSummaryText}
                  </p>
                </IonCardContent>
              )}
            </IonCard>
          )}

          {/* ── Sponsors Card ────────────────────────────────────── */}
          {sponsors.length > 0 && (
            <IonCard className="mb-3">
              <IonCardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <IonIcon icon={personOutline} color="primary" />
                  <IonCardTitle className="text-base">{t('billDetail.sponsors')}</IonCardTitle>
                </div>
              </IonCardHeader>
              <IonCardContent className="pt-0">
                <IonList
                  lines="none"
                  style={{ background: 'transparent', '--ion-item-background': 'transparent' } as React.CSSProperties}
                >
                  {sponsors.map((sponsor, idx) => (
                    <IonItem
                      key={idx}
                      button={!!sponsor.legislator_id}
                      onClick={() => {
                        if (sponsor.legislator_id) {
                          navigate(`/pro/legislators/${sponsor.legislator_id}`);
                        }
                      }}
                    >
                      <IonLabel>
                        <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          {sponsor.name}
                        </h3>
                        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          {sponsor.sponsor_type === 'Primary' ? t('billDetail.primarySponsorLabel') : t('billDetail.coSponsorLabel')}
                        </p>
                      </IonLabel>
                      {sponsor.party && (
                        <span
                          slot="end"
                          className={`flex-shrink-0 text-xs font-semibold ${getPartyTextClasses(sponsor.party)}`}
                        >
                          {formatPartyDisplay(sponsor.party)}
                        </span>
                      )}
                    </IonItem>
                  ))}
                </IonList>
              </IonCardContent>
            </IonCard>
          )}

          {/* ── Lobbyist Positions Card ──────────────────────────── */}
          {lobbyists.length > 0 && (
            <IonCard className="mb-3">
              <IonCardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IonIcon icon={briefcaseOutline} color="primary" />
                    <IonCardTitle className="text-base">{t('billDetail.lobbyistPositions')}</IonCardTitle>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {supportCount > 0 && (
                      <IonChip color="success" className="text-xs h-6">
                        {supportCount} {t('billDetail.support')}
                      </IonChip>
                    )}
                    {opposeCount > 0 && (
                      <IonChip color="danger" className="text-xs h-6">
                        {opposeCount} {t('billDetail.oppose')}
                      </IonChip>
                    )}
                  </div>
                </div>
              </IonCardHeader>
              <IonCardContent className="pt-0">
                <IonList
                  lines="inset"
                  style={{ background: 'transparent', '--ion-item-background': 'transparent' } as React.CSSProperties}
                >
                  {displayedLobbyists.map((lob: any, idx: number) => (
                    <IonItem key={lob.id || idx}>
                      <IonLabel className="ion-text-wrap">
                        <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                          {lob.lobbyist_name}
                        </h3>
                        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          {[lob.lobbyist_firm_name, lob.client_name].filter(Boolean).join(' · ')}
                        </p>
                        {lob.position && (
                          <div className="mt-1.5">
                            <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${getPositionColorClasses(lob.position)}`}>
                              {lob.position}
                            </span>
                          </div>
                        )}
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
                {lobbyists.length > LOBBYIST_INITIAL_COUNT && (
                  <IonButton
                    fill="clear"
                    expand="block"
                    size="small"
                    className="mt-1"
                    onClick={() => setLobbyistsExpanded(!lobbyistsExpanded)}
                  >
                    <IonIcon
                      slot="start"
                      icon={lobbyistsExpanded ? chevronUpOutline : chevronDownOutline}
                    />
                    {lobbyistsExpanded
                      ? t('common.showLess')
                      : t('bills.showAllLobbyists', { count: lobbyists.length })
                    }
                  </IonButton>
                )}
              </IonCardContent>
            </IonCard>
          )}

        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center px-4">
          <p className="text-text-secondary dark:text-text-dark-secondary">{t('billDetail.billNotFound')}</p>
          <IonButton fill="clear" size="small" className="mt-3" onClick={() => navigate('/pro/bills')}>
            {t('common.backToBills')}
          </IonButton>
        </div>
      )}

      {/* ── Position Selector ─────────────────────────────────── */}
      <ProPositionSheet
        isOpen={positionSelectorOpen}
        currentPosition={currentPosition}
        onSelect={handlePositionSelect}
        onDismiss={() => setPositionSelectorOpen(false)}
        type="bill"
      />

      {/* ── Tracking workspace picker ────────────────────────── */}
      <ProTrackingWorkspacePicker
        isOpen={pickerOpen}
        eligible={eligible}
        selectedId={workspaceId}
        onSelect={handlePickerSelect}
        onDismiss={handlePickerDismiss}
      />

      {/* ── Toast ─────────────────────────────────────────────── */}
      <IonToast
        isOpen={!!showToast}
        message={showToast}
        duration={2500}
        onDidDismiss={() => setShowToast('')}
        position="bottom"
      />
    </>
  );
};

export default ProBillDetailPage;
